import React, { useState, useRef, useCallback, useEffect } from 'react';

const COLLAPSED_HEIGHT = 180;
const PEEK_HEIGHT = 180;

export default function BottomSheet({ children }) {
  const [expanded, setExpanded] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [sheetHeight, setSheetHeight] = useState(COLLAPSED_HEIGHT);
  const startY = useRef(0);
  const startHeight = useRef(COLLAPSED_HEIGHT);
  const sheetRef = useRef(null);

  const maxHeight = typeof window !== 'undefined' ? window.innerHeight * 0.85 : 600;

  const onTouchStart = useCallback((e) => {
    setDragging(true);
    startY.current = e.touches[0].clientY;
    startHeight.current = sheetHeight;
  }, [sheetHeight]);

  const onTouchMove = useCallback((e) => {
    if (!dragging) return;
    const delta = startY.current - e.touches[0].clientY;
    const newHeight = Math.max(PEEK_HEIGHT, Math.min(maxHeight, startHeight.current + delta));
    setSheetHeight(newHeight);
  }, [dragging, maxHeight]);

  const onTouchEnd = useCallback(() => {
    setDragging(false);
    // Snap to collapsed or expanded
    const midpoint = (COLLAPSED_HEIGHT + maxHeight) / 2;
    if (sheetHeight > midpoint) {
      setSheetHeight(maxHeight);
      setExpanded(true);
    } else {
      setSheetHeight(COLLAPSED_HEIGHT);
      setExpanded(false);
    }
  }, [sheetHeight, maxHeight]);

  useEffect(() => {
    if (!dragging) return;
    const handleMove = (e) => onTouchMove(e);
    const handleEnd = () => onTouchEnd();
    document.addEventListener('touchmove', handleMove, { passive: true });
    document.addEventListener('touchend', handleEnd);
    return () => {
      document.removeEventListener('touchmove', handleMove);
      document.removeEventListener('touchend', handleEnd);
    };
  }, [dragging, onTouchMove, onTouchEnd]);

  return (
    <div
      className={`bottom-sheet ${expanded ? 'bottom-sheet-expanded' : ''}`}
      ref={sheetRef}
      style={{
        height: sheetHeight,
        transition: dragging ? 'none' : 'height 0.3s ease',
      }}
    >
      <div className="bottom-sheet-handle" onTouchStart={onTouchStart}>
        <div className="bottom-sheet-bar" />
      </div>
      <div className="bottom-sheet-content">
        {children}
      </div>
    </div>
  );
}
